<template>
  <div>
    <div
      v-if="!editing"
      :draggable="canEdit && !dragDisabled"
      :style="`padding-left:${depth === 0 ? 40 : depth * 15 + 40}px;`"
      :data-testid="`feature-card-${feature.id}`"
      :class="`feature-card
        ${highlighted ? 'highlighted' : ''}
        ${dragHover ?? ''}
        ${dragging ? 'feature-card-hide' : ''}
      `"
      @dragstart.stop="featureDrag"
      @dragend="dragEnd"
      @dragover.stop.prevent="toggleDragClass"
      @dragenter.stop.prevent="toggleDragClass"
      @dragleave.stop.prevent="dragHover = null;"
      @drop.stop="featureDrop"
      @mouseover="highlight(true)"
      @mouseout="highlight(false)"
      @mouseenter="hovered = true"
      @mouseleave="hovered = false"
      @click="cardClick"
    >
      <!-- Sits in the card's left padding so the icon and name stay aligned with cards that have no caret -->
      <CaretIcon
        v-if="hasLinkedLabels"
        class="linked-labels-toggle"
        data-testid="linked-labels-toggle"
        :style="`transform: rotate(${linkedLabelsExpanded ? 0 : -90}deg)`"
        @click.stop="$emit('toggleLinkedLabels')"
      />
      <div
        class="feature-icon-container"
        :class="setClass(feature?.featureType)"
      >
        <component
          :is="(canEdit && !dragDisabled && hovered) ? 'DragIcon' : getIcon(feature?.featureType)"
          :style="{
            '--feature-color': featureColor,
            'fill': feature?.featureType === 'placeMarker' && !hovered && getIconColor()
          }"
          class="feature-icon"
        />
        <LabelIcon
          v-if="hasLinkedLabels"
          class="linked-label-badge"
          data-testid="linked-label-badge"
        />
      </div>
      <span
        ref="featureDisplayName"
        class="feature-card-name"
        :style="feature?.visible ? '' : 'padding-left: 0px'"
      >
        {{ displayName }}
      </span>
      <div class="action-items">
        <v-btn
          :icon="feature.visible ? `mdi-eye-outline` : `mdi-eye-off-outline`"
          data-testid="visibility-icon"
          variant="text"
          class="action-icon"
          :class="!feature.visible ? 'action-icon-persist' : ''"
          aria-label="Toggle Visibility"
          @click.stop="toggleVisibility"
        />
        <v-btn
          v-if="canEdit"
          icon="mdi-delete-outline"
          variant="text"
          class="action-icon"
          :class="!feature.visible ? 'action-icon-invisible' : ''"
          data-testId="feature-delete-icon"
          aria-label="Delete Feature"
          @click.stop="deleteFeature"
        />
      </div>
    </div>
    <div
      v-else
      class="input-container"
    >
      <input
        ref="featureName"
        v-model="featureName"
        data-testId="feature-edit-input"
        class="input outlined-input"
        type="text"
        @blur="saveFeatureName()"
        @keyup.enter="saveFeatureName()"
      >
    </div>
  </div>
</template>

<script>
// 3rd Party Dependencies
import {mapState, mapActions, mapMutations} from 'vuex';

// Internal Dependencies
import {Feature, featureOptions, getFeatureIcon} from '@/services/OverlaysService';
import {MapService} from '@/services/MapService';
import {OverlayService} from '@/services/OverlaysService';
import {markerIconMap} from '@/services/MapService/Icons';
import {preventLetterClipping} from '@/lib/ReusableFunctions/Formatters';

// Vue Components
import CaretIcon from '@/components/svgs/CaretIcon.vue';
import DragIcon from '@/components/svgs/DragIcon.vue';
import LabelIcon from '@/components/svgs/LabelIcon.vue';
import {blueGray} from '@/lib/colors';
import BlockPlanIcon from '@/components/svgs/ToolboxIcons/BlockPlanIcon.vue';

/** @typedef {import('@/services/GraphicService/Graphic').Graphic} Graphic */

const DOUBLECLICK_DELAY = 275;

export default {
  name: 'FeatureResultCard',
  components: {
    CaretIcon,
    DragIcon,
    LabelIcon,
    BlockPlanIcon,
  },
  inject: [
    'addMenuClickListener',
    'removeMenuClickListener',
    'useLocalListeners',
  ],
  props: {
    feature: {
      type: Feature,
      required: true,
    },
    depth: {
      type: Number,
      default: 0,
    },
    dragHandler: {
      type: Function,
      default: () => {},
    },
    dropHandler: {
      type: Function,
      default: () => {},
    },
    canEdit: {
      type: Boolean,
      required: true,
    },
    hasLinkedLabels: {
      type: Boolean,
      default: false,
    },
    linkedLabelsExpanded: {
      type: Boolean,
      default: false,
    },
    dragDisabled: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['deleteFeature', 'selectFeature', 'toggleLinkedLabels'],
  data() {
    return {
      /** @type {boolean} */
      editing: false,
      /** @type {string} */
      featureName: '',
      /** @type {{eventName: string, handlerName: string, handler: () => any}[]} */
      events: [],
      /** @type {boolean} */
      highlighted: false,
      /** @type {boolean} */
      hovered: false,
      /** @type {number|null} */
      timeoutID: null,
      /** @type {Graphic|null} */
      graphic: null,
      /** @type {boolean} */
      dragging: false,
      /** @type {boolean} */
      dragHover: null,
      /** @type {string} */
      featureColor: blueGray,
      /** @type {boolean} */
      isSelected: false,
      highlight: (/** @type {boolean} */newState) => {},
    };
  },
  computed: {
    ...mapState('infowindow', ['selectedContentId']),
    displayName() {
      let displayName = this.featureName;
      // If the feature name is empty, display the default name
      if (!displayName?.length) {
        displayName = featureOptions.get(this.feature.featureType)?.defaultName ?? 'Untitled Feature';
      }
      return displayName;
    },
  },
  watch: {
    'feature.name': function(newVal) {
      this.featureName = newVal;
    },
    'feature.icon.color': function(newVal) {
      this.featureColor = newVal ?? blueGray;
    },
    'selectedContentId': function(newVal) {
      const selected = this.feature.id === newVal;
      this.isSelected = selected;
      this.highlight(selected);
    },
    async editing(newVal) {
      if (!newVal) {
        await this.clipFeatureNameText();
      }
    },
  },
  created() {
    /** @type {number} */
    let timeoutId;
    // Debounce the highlight function because the nested components fire mouseover and mouseout events on every
    // transition causing a call for highlight(false) immediately followed by highlight(true).
    this.highlight = (/** @type {boolean} */newState) => {
      clearTimeout(timeoutId);
      timeoutId = window.setTimeout(() => {
        this._highlight(newState);
      }, 20);
    };
  },
  async beforeMount() {
    this.featureName = this.feature.name;
    this.featureColor = this.feature?.icon?.color ?? blueGray;
    if (this.useLocalListeners) {
      try {
        this.graphic = await this.feature.graphic;
      } catch (error) {
        console.error(`Error mounting FeatureResultCard, cannot get graphic for feature ${this.feature.name} (${this.feature.id}) in overlay ${this.feature.parent.name} (${this.feature.parent.id})`, error);
      }
      this.addLocalHighlightListeners();
    }
  },
  async mounted() {
    this.addMenuClickListener?.(`handle_menu_click_${this.feature.id}`, this.handleMenuClick);
    await this.clipFeatureNameText();
  },
  async beforeUnmount() {
    this.removeMenuClickListener?.(`handle_menu_click_${this.feature.id}`);
    if (this.events.length) {
      // Remove listeners set up and used by feature cards
      this.events.forEach(({eventName, handlerName, handler}) => {
        this.feature.removeHandler(eventName, handlerName, handler);
      });
      this.events = [];
      this.graphic?.setHighlight(false);
      MapService.removeMapListener(`stop_editing_feature_${this.feature.id}`, 'click');
    }
    // the graphic may have turned off editing at the graphic level, so we need to make sure to turn it off here
    await this.feature.setEditing(false);
  },
  methods: {
    ...mapMutations('infowindow', ['updateFeatureName']),
    ...mapActions('infowindow', ['closeInfoWindow']),
    addLocalHighlightListeners() {
      // Create handler for mousing over while editing
      const mouseoverHandler = () => {
        // Stifle errors occuring when the graphic is still null
        try {
          this.highlight(true);
        } catch (e) {
          console.warn(`Couldn't set highlight`, e);
        }
      };
      this.feature.addHandler('mouseover', 'overlayFeatureMouseoverHighlightCard', mouseoverHandler);
      // Create handler for mousing out while editing
      const mouseoutHandler = () => {
        // Stifle errors occuring when the graphic is still null
        try {
          this.highlight(false);
        } catch (e) {
          console.warn(`Couldn't set highlight`, e);
        }
      };
      this.feature.addHandler('mouseout', 'overlayFeatureMouseoutHighlightCard', mouseoutHandler);
      // Set event listeners on graphic for highlighting graphic on mouseover, mouseout
      this.events.push({
        eventName: 'mouseover',
        handlerName: 'overlayFeatureMouseoverHighlightCard',
        handler: mouseoverHandler,
      });
      this.events.push({
        eventName: 'mouseout',
        handlerName: 'overlayFeatureMouseoutHighlightCard',
        handler: mouseoutHandler,
      });
    },
    /**
     * Sets a specific class depending on feature type.
     * @param {string} featureType
     * @return {"" | "placemarker-icon" | "polygon-icon"}
     */
    setClass(featureType) {
      switch (featureType) {
        case 'placeMarker': {
          return 'placemarker-icon';
        }
        case 'polygon': {
          return 'polygon-icon';
        }
        default: {
          return '';
        }
      }
    },
    getIcon(type) {
      if (type === 'placeMarker' && this.feature?.icon?.name) {
        // Display the marker SVG only for marker features
        return markerIconMap.get(this.feature.icon.name)?.svg;
      }
      return getFeatureIcon(type).raw;
    },
    getIconColor() {
      if (!this.feature?.icon?.name) return;
      return markerIconMap.get(this.feature?.icon?.name)?.hex;
    },
    /**
     * Handle clicks on the card. Uses timeout to distinguish between single and double clicks.
     * @param {Event} event The triggering event.
     */
    async cardClick(event) {
      // close the info window when the card is clicked
      this.closeInfoWindow();

      // If the map is not editable, then clicking on a feature card should only open the info window.
      if (!this.canEdit) {
        if (!this.graphic) {
          this.graphic = await this.feature.graphic;
        }
        this.feature.openInfoWindow({
          payload: {
            toolbar: {toolbarOptions: []},
            location: this.graphic?.position,
          },
        });
        // Might need to move this to SharedMap service as external maps doesn't need Overlay code
        OverlayService.panAndZoomToFeature(this.feature);
        return;
      }

      if (!this.timeoutID) { // if no timeout is set, this is the first click, possibly a single click
        this.timeoutID = window.setTimeout(async () => {
          // if the timeout remains set after 200ms, then no other click was received.
          this.timeoutID = null;
          if (!this.feature.visible) return;
          // single click actions
          await this.feature.setEditing(true);
          OverlayService.panAndZoomToFeature(this.feature);

          // Set up listener to stop editing feature on Map click.
          const name = `stop_editing_feature_${this.feature.id}`;
          MapService.addMapListener(name, 'click', () => {
            this.feature.setEditing(false);
            MapService.removeMapListener(name);
          });
        }, DOUBLECLICK_DELAY);
      } else { // if a timeout is already set, then this is the second click within 200ms, so it's a double click
        clearTimeout(this.timeoutID);
        this.timeoutID = null;
        // double click actions
        this.setEdit(true);
      }
    },
    handleMenuClick() {
      // Stop editing the current feature.
      this.feature?.setEditing(false);
    },
    async setEdit(edit) {
      this.editing = edit;
      this.highlighted = false;
      if (edit) {
        await this.$nextTick();
        this.$refs.featureName.focus();
      }
    },
    async saveFeatureName() {
      this.setEdit(false);
      // If the new name is the same as the current name, do nothing
      if (this.featureName === this.feature.name) return;
      await this.feature.update({name: this.featureName});
      // Notify the InfoWindow that the Feature name has changed via the store
      this.updateFeatureName(this.featureName);
    },
    deleteFeature() {
      this.$emit('deleteFeature', this.feature.id);
    },
    /**
     * Turn highlighting on or off for the feature. If the feature is selected, turning off highlighting
     * will have no effect
     * @param {boolean} newState - whether to highlight the feature or not
     */
    async _highlight(newState) {
      try {
        if (!this.graphic) {
          this.graphic = await this.feature.graphic;
        }
      } catch (error) {
        console.error(`Cannot get graphic for feature ${this.feature.name} (${this.feature.id}) in ${this.feature.parent.name} (${this.feature.parent.id})`, error);
      }
      try {
        // do not turn off highlight when feature is selected or if the feature is a placemarker. Playing the size
        // change animation on hover for placemarkers is not desirable, so don't highlight them at all.
        if ((this.isSelected && !newState)) return;
        this.highlighted = newState;
        if (this.feature.featureType === 'placeMarker') return;
        this.graphic?.setHighlight(newState);
      } catch (err) {
        console.warn(`${this.feature.id} could not be highlighted: ${err}`);
        this.highlighted = false;
      }
    },
    /**
     * Calls the feature's `setVisibility` method to toggle current visibility.
     * This is called with the inverse of this.feature.visible because the setVisibility method needs to accept a
     * boolean parameter for other cases.
     */
    async toggleVisibility() {
      const newState = !this.feature.visible;
      if (this.feature.isEditing && !newState) {
        await this.feature.setEditing(false);
      }
      this.feature.setVisibility(newState);
      if (this.canEdit) {
        this.feature.setDefaultVisibility(newState);
      }
    },
    /**
     * @param {DragEvent} e The event object deriving from dragstart
     * Sets the local `dragging` piece in state for conditional classes and calls the
     * dragHandler callback function with the event object. `setTimeout` is necessary here
     * so that the appropriate classes are applied to the psuedo element shown while dragging
     */
    featureDrag(e) {
      setTimeout(() => {
        this.dragging = true;
      }, 0);
      this.dragHandler(e);
    },
    /**
     * @param {DragEvent} e Event object derived from the drop event
     * Cleans up local state and calls the dropHandler callback function
     */
    featureDrop(e) {
      this.dragEnd();
      if (this.dragDisabled) return;
      const offset = e.offsetY >= (e.target.offsetHeight / 2) ? 1 : -1;
      this.dropHandler(e, offset);
    },
    // Handles cleaning up local drag state data
    dragEnd() {
      this.dragHover = null;
      this.dragging = false;
    },
    toggleDragClass(e) {
      if (this.dragDisabled) return;
      if (e?.offsetY >= 0) {
        this.dragHover = e.offsetY >= (e.target.offsetHeight / 2) ? 'drop-bottom' : 'drop-top';
      } else {
        this.dragHover = null;
      }
    },
    /**
     * Fits the feature name text to the container width without clipping letters.
     */
    async clipFeatureNameText() {
      // Wait for the next tick to ensure the text has been rendered after editing is set to false
      await this.$nextTick();
      const ref = this.$refs.featureDisplayName;
      // For each depth, subtract 15px from the textContainerWidth
      const textContainerWidth = 253 - (this.depth * 15);
      preventLetterClipping(ref, textContainerWidth);
    },
  },
};
</script>

<style scoped lang="scss">
.feature-card {
  width: 100%;
  height: 48px;
  display: flex;
  align-items: center;
  padding-left: 40px;
  margin-bottom: 3px;
  font-weight: 500;

  .linked-labels-toggle {
    flex: none;
    height: 20px;
    width: 20px;
    margin-left: -24px;
    margin-right: 4px;
  }

  .feature-icon-container {
    position: relative;
    height: 20px;
    width: 20px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-right: 10px;

    svg {
      height: 20px;
      width: 20px;
      fill: main.$npGray;
    }
  }

  // We want different heights for different icon types
  .polygon-icon {
    height: 21px;
    width: 21px;

    svg {
      height: 21px;
      width: 21px;
      margin: -5px 3px 0 -3px;
    }
  }

  .placemarker-icon {
    height: 24px;
    width: 24px;

    svg {
      height: 24px;
      width: 24px;
      margin: -2px 3px 0 -3px;
    }
  }

  .feature-icon {
    margin: -6px 3px 0 -3px;
  }

  .feature-icon-container .linked-label-badge {
    position: absolute;
    right: -5px;
    bottom: -4px;
    height: 10px;
    width: 10px;
    margin: 0;
    background-color: white;
    border-radius: 2px;
  }

  .action-items {
    display: flex;
    flex-direction: row;
    align-items: center;
    padding-right: 2px;
    margin-left: auto;
    margin-right: 10px;
  }

  &-name {
    white-space: nowrap;
    overflow: hidden;
  }
  .action-icon {
    padding: 0px;
    margin-left: 5px;
    width: 24px;
    height: 24px;
    display: none;

    &-persist {
      display: flex;
    }
    &-invisible {
      display: flex;
      visibility: hidden;
    }

    &:hover {
      // make the hover background larger than the button itself
      :deep(.v-btn__overlay) {
        opacity: 15%;
        top: -4px;
        left: -5px;
        width: 140%;
        height: 140%;
      }
    }
  }

  &:hover {
    background-color: main.$lightGray;
    cursor: pointer;
    .action-icon {
      display: flex;
      visibility: visible;

      &-persist {
        margin-right: 0;
      }
    }
  }

  &-hide {
    background-color: main.$lightGray;
    opacity: .8;

    .action-items {
      visibility: hidden;
    }
  }
}
.highlighted {
  background-color: main.$lightGray;
}

.input-container {
  height: 48px;
  width: 100%;
  padding: 0 20px 0 40px;
  display: flex;
  justify-items: center;
  align-items: center;
  & .input {
    height: 35px;
    width: 100%;
    padding: 7px;
  }
}

.drop-top {
  border-top: solid 2px main.$linkBlue;
}
.drop-bottom {
  border-bottom: solid 2px main.$linkBlue;
}
</style>
